import React, { useState, useEffect } from 'react';
import RecipeCard from './RecipeCard';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';
import styles from './MyFavorites.module.css';
import Masonry from 'react-masonry-css';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { Spinner } from 'react-bootstrap';

function MyFavorites({ sessionToken, searchTerm = '', onMealAdded }) {
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showError, setShowError] = useState(false);
    const [pantryIngredients, setPantryIngredients] = useState(null);
    const { pgUser } = useAuth();
    const { sessionToken: authSessionToken, setSessionToken } = useAuth();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();

    //if session token not passed from parent, get it from auth context

    useEffect(() => {
        if (!authSessionToken) {
            const getSessionToken = async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    setSessionToken(session.access_token);
                }
            };
            getSessionToken();
        }
    }, [authSessionToken]);

    // Fetch pantry ingredients for pantry match calculation
    useEffect(() => {
        const fetchPantryIngredients = async () => {
            if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
            
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
                // Silently fail - pantry match just won't show
            }
        };

        fetchPantryIngredients();
    }, [sessionToken, pgUser?.pantry_id, pgUser?.id]);

    useEffect(() => {
        const fetchFavoritesAndDislikes = async () => {

            console.log("fetchFavoritesAndDislikes called with sessionToken: ", sessionToken);
            if (!sessionToken || !pgUser) return;

            try {
                // Fetch favorites
                const response = await fetch('/api/v1/recipe-favorite/my-favorites/with-ingredients', {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch favorites: ${response.status}`);
                }

                const data = await response.json();
                console.log('Favorites data:', data);
                if (Array.isArray(data)) {
                    setFavoriteRecipes(data);
                } else {
                    console.error('Invalid data format:', data);
                    setFavoriteRecipes([]);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (err) {
                console.error('Error fetching favorites/dislikes.');
                setError(err.message || 'Failed to load favorite recipes');
            } finally {
                setIsLoading(false);
            }
        };

        fetchFavoritesAndDislikes();
    }, [pgUser, sessionToken, getAllDislikedRecipes]); // Refetch when pgUser or sessionToken changes

    if (isLoading) {
        return (
            <div className="text-center my-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading your favorite recipes...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mt-4">
                <p className="mb-1">Error loading your favorite recipes. Please refresh the page or try again later.</p>
                
            </div>
        );
    }

    if (favoriteRecipes.length === 0) {
        return <div>You haven't favorited any recipes yet.</div>;
    }

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        500: 1  // Changed to 1 column for very small screens
    };

    // If there's only one recipe, force single column layout
    if (favoriteRecipes.length === 1) {
        breakpointColumns.default = 1;
    }

    // Filter recipes based on search term
    const filteredRecipes = favoriteRecipes.filter(favorite => {
        const searchLower = searchTerm.toLowerCase();
        const recipe = favorite.recipe;
        
        if (!recipe) return false;
        
        // Search by recipe name
        if (recipe.name?.toLowerCase().includes(searchLower)) {
            return true;
        }
        
        // Search by ingredient names
        if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
            return recipe.ingredients.some(ingredient => 
                ingredient.ingredient_name && 
                ingredient.ingredient_name.toLowerCase().includes(searchLower)
            );
        }
        
        return false;
    });

    if (filteredRecipes.length === 0 && searchTerm) {
        return <div className="text-center my-5">No favorite recipes found matching "{searchTerm}"</div>;
    }

    return (
        <div className={styles.container}>
            <Masonry
                breakpointCols={breakpointColumns}
                className="my-masonry-grid"
                columnClassName="my-masonry-grid_column"
            >
                {filteredRecipes.map(favorite => (
                    // Pass the recipe data from the nested 'recipe' object to RecipeCard
                    <div 
                        key={favorite.recipe_id} 
                        className={styles.masonryItem}
                        style={{ 
                            minWidth: '250px',
                            width: '100%'
                        }}
                    >
                        <RecipeCard 
                            recipe={favorite.recipe}
                            isFavorite={true}
                            isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(favorite.recipe_id))}
                            pantryIngredients={pantryIngredients}
                            onMealAdded={onMealAdded}
                        />
                    </div>
                ))}
            </Masonry>
        </div>
    );
}

export default MyFavorites;